/**
 * AdminLogin.jsx — Iter 57
 * Dedicated /admin/login. Reuses POST /api/auth/login; on success, checks
 * user.is_super_admin || user.studio_admin, else denies with a friendly message.
 */
import React, { useState } from 'react';
import api from '@/api';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ShieldCheck, LockKey, ArrowRight } from '@phosphor-icons/react';

const AdminLogin = ({ setUser }) => {
  const nav = useNavigate();
  const [form, setForm] = useState({ email: '', password: '' });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const r = await api.post('/auth/login', form);
      const u = r.data.user;
      if (!(u.is_super_admin || u.studio_admin)) {
        toast.error('This account has no admin access.'); setBusy(false); return;
      }
      localStorage.setItem('token', r.data.token);
      localStorage.setItem('user', JSON.stringify(u));
      setUser(u);
      toast.success('Welcome back');
      nav('/admin');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#0a0e14] text-slate-100 flex items-center justify-center p-4" data-testid="admin-login-page">
      <div className="w-full max-w-md bg-[#111827] border border-slate-800 rounded-2xl p-8">
        <div className="flex items-center gap-2 mb-8">
          <ShieldCheck size={22} weight="fill" className="text-emerald-400" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-emerald-400 font-bold">Founderboard</div>
            <h1 className="text-lg font-heading font-bold">Admin Console</h1>
          </div>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block text-[11px] uppercase tracking-wider font-bold text-slate-400 mb-1.5">Email</label>
            <input type="email" required value={form.email} data-testid="admin-login-email"
              onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
              className="w-full bg-[#0a0e14] border border-slate-800 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-emerald-500" />
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-wider font-bold text-slate-400 mb-1.5">Password</label>
            <input type="password" required value={form.password} data-testid="admin-login-password"
              onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
              className="w-full bg-[#0a0e14] border border-slate-800 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-emerald-500" />
          </div>
          <button type="submit" disabled={busy} data-testid="admin-login-submit"
            className="w-full inline-flex items-center justify-center gap-1.5 bg-emerald-500 hover:bg-emerald-400 text-black font-semibold px-4 py-2.5 rounded-lg disabled:opacity-50">
            <LockKey size={13} weight="bold" /> {busy ? 'Authenticating…' : 'Sign in to Admin'}
          </button>
        </form>
        <div className="mt-6 pt-6 border-t border-slate-800 text-xs text-slate-500 flex justify-between">
          <span>Don&rsquo;t have access?</span>
          <Link to="/admin/signup" className="text-emerald-400 hover:text-emerald-300 inline-flex items-center gap-0.5" data-testid="admin-signup-link">Request admin access <ArrowRight size={9} weight="bold" /></Link>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
